import type { Control, FieldErrors, UseFormRegister } from "react-hook-form";

import FormInputSelect from "components/Form/Input/Select";
import FormInputSortCode from "components/Form/Input/SortCode";
import FormInputText from "components/Form/Input/Text";
import type { ConsolidationItem, DebtConsolidationFormValues } from "../types";
import FormInputCurrency from "components/Form/Input/Currency";
import {
  PAID_TO_CUSTOMER,
  PAID_TO_OPTIONS,
  TH_STYLE,
  TD_STYLE,
} from "../consts";

type Props = {
  fields: Array<{ id: string }>;
  creditProviders: ConsolidationItem[];
  control: Control<DebtConsolidationFormValues>;
  register: UseFormRegister<DebtConsolidationFormValues>;
  errors: FieldErrors<DebtConsolidationFormValues>;
};

export default function ConsolidatedCreditTable({
  fields,
  creditProviders,
  control,
  register,
  errors,
}: Props) {
  return (
    <div>
      <div className="mb-4">
        <h3 className="text-md mb-1 font-semibold">Consolidated Credit</h3>
        <p className="mb-2 text-sm text-brand-copy-light">
          Please complete the credit provider, consolidation amount, sort code,
          account number and reference and who the funds will be paid to for
          each credit.
        </p>
      </div>

      <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th scope="col" className={TH_STYLE}>
                  Applicant
                </th>
                <th scope="col" className={TH_STYLE}>
                  Type
                </th>
                <th scope="col" className={`${TH_STYLE} bg-brand-green-light`}>
                  Credit Provider
                </th>
                <th scope="col" className={`${TH_STYLE} bg-brand-green-light`}>
                  Consolidation Amount
                </th>
                <th scope="col" className={`${TH_STYLE} bg-brand-green-light`}>
                  Account Number
                </th>
                <th scope="col" className={`${TH_STYLE} bg-brand-green-light`}>
                  Sort Code
                </th>
                <th scope="col" className={`${TH_STYLE} bg-brand-green-light`}>
                  Reference
                </th>
                <th scope="col" className={`${TH_STYLE} bg-brand-green-light`}>
                  Paid To
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {fields.map((field, index) => {
                const c = creditProviders[index];
                if (!c) {
                  return null;
                }
                const isCashOut = c.paidTo === PAID_TO_CUSTOMER;
                return (
                  <tr key={field.id}>
                    <td className={`${TD_STYLE} pl-3 text-base font-medium`}>
                      <div className="mt-3 text-base">
                        {c.applicant.forename} {c.applicant.surname}
                      </div>
                    </td>
                    <td className={`${TD_STYLE}`}>
                      <div className="mt-2.5 inline-block rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-base">
                        {c.providerType}
                      </div>
                    </td>
                    <td
                      className={`${TD_STYLE} bg-brand-green-light bg-opacity-20`}
                    >
                      <FormInputText
                        id={`creditProviders.${index}.provider`}
                        placeholder="e.g. Barclays"
                        register={register}
                        requiredField={false}
                        error={
                          errors.creditProviders?.[index]?.provider?.message
                        }
                      />
                    </td>
                    <td
                      className={`${TD_STYLE} bg-brand-green-light bg-opacity-20`}
                    >
                      <FormInputCurrency
                        id={`creditProviders.${index}.settlementAmount`}
                        placeholder="1000"
                        register={register}
                        requiredField={false}
                        error={
                          errors.creditProviders?.[index]?.settlementAmount
                            ?.message
                        }
                      />
                    </td>
                    <td
                      className={`${TD_STYLE} bg-brand-green-light ${
                        isCashOut
                          ? "bg-opacity-10 opacity-40"
                          : "bg-opacity-20 opacity-100"
                      }`}
                    >
                      <FormInputText
                        id={`creditProviders.${index}.accountNumber`}
                        placeholder="01234567"
                        register={register}
                        disabled={isCashOut}
                        requiredField={false}
                        error={
                          errors.creditProviders?.[index]?.accountNumber
                            ?.message
                        }
                      />
                    </td>
                    <td
                      className={`${TD_STYLE} bg-brand-green-light ${
                        isCashOut
                          ? "bg-opacity-10 opacity-40"
                          : "bg-opacity-20 opacity-100"
                      }`}
                    >
                      <FormInputSortCode
                        control={control}
                        name={`creditProviders.${index}.sortCode`}
                        id={`creditProviders.${index}.sortCode`}
                        disabled={isCashOut}
                      />
                    </td>
                    <td
                      className={`${TD_STYLE} bg-brand-green-light ${
                        isCashOut
                          ? "bg-opacity-10 opacity-40"
                          : "bg-opacity-20 opacity-100"
                      }`}
                    >
                      <FormInputText
                        id={`creditProviders.${index}.reference`}
                        placeholder="Reference"
                        register={register}
                        disabled={isCashOut}
                        requiredField={false}
                        error={
                          errors.creditProviders?.[index]?.reference?.message
                        }
                      />
                    </td>
                    <td
                      className={`${TD_STYLE} bg-brand-green-light bg-opacity-20 pr-3`}
                    >
                      <FormInputSelect
                        id={`creditProviders.${index}.paidTo`}
                        options={PAID_TO_OPTIONS}
                        register={register}
                        requiredField={false}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
